import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Organization Analytics - Team insights dashboard
 *
 * Features:
 * - Meeting volume over time
 * - Time analytics
 * - Guest analytics
 * - Top contributors
 */

export default function OrgAnalytics({ organization, members, stats }) {
  const [analyticsData, setAnalyticsData] = useState({
    meetingsByDay: [],
    topHosts: [],
    peakHours: Array(24).fill(0),
    guestStats: { total: 0, unique: 0, repeat: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    if (organization?.id) {
      loadAnalytics();
    }
  }, [organization?.id, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const meetingsRef = collection(db, `organizations/${organization.id}/meetings`);
      const q = query(meetingsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const now = new Date();
      const rangeStart = new Date();
      if (timeRange === 'week') rangeStart.setDate(now.getDate() - 7);
      else if (timeRange === 'month') rangeStart.setMonth(now.getMonth() - 1);
      else rangeStart.setFullYear(now.getFullYear() - 1);

      // Process meetings
      const hostCounts = {};
      const peakHours = Array(24).fill(0);
      const meetingsByDay = {};
      let totalGuests = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || new Date();

        if (createdAt >= rangeStart) {
          // Count by host
          const host = data.createdBy;
          hostCounts[host] = (hostCounts[host] || 0) + 1;

          // Peak hours
          peakHours[createdAt.getHours()]++;

          // Meetings by day
          const dayKey = createdAt.toISOString().split('T')[0];
          meetingsByDay[dayKey] = (meetingsByDay[dayKey] || 0) + 1;

          // Guest count
          totalGuests += data.guestCount || 0;
        }
      });

      // Top hosts
      const topHosts = Object.entries(hostCounts)
        .map(([userId, count]) => {
          const member = members.find(m => m.userId === userId || m.id === userId);
          return { userId, count, email: member?.email || 'Unknown' };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Format meetings by day for chart
      const sortedDays = Object.entries(meetingsByDay)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setAnalyticsData({
        meetingsByDay: sortedDays,
        topHosts,
        peakHours,
        guestStats: { total: totalGuests, unique: 0, repeat: 0 }
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Find peak hour
  const peakHour = analyticsData.peakHours.indexOf(Math.max(...analyticsData.peakHours));
  const formatHour = (hour) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h}${ampm}`;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Analytics</h2>
          <p className="text-white/50 text-sm mt-1">Team usage insights and trends</p>
        </div>

        {/* Time Range Filter */}
        <div className="flex gap-2">
          {['week', 'month', 'year'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-[#06c] text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {range === 'week' ? '7 Days' : range === 'month' ? '30 Days' : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#06c] rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15">
              <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Total Meetings</div>
              <div className="text-3xl font-bold text-white">{stats?.totalMeetings || 0}</div>
            </div>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15">
              <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Total Hours</div>
              <div className="text-3xl font-bold text-white">{Math.floor((stats?.totalMinutes || 0) / 60)}h</div>
            </div>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15">
              <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Peak Hour</div>
              <div className="text-3xl font-bold text-white">{formatHour(peakHour)}</div>
            </div>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/15">
              <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Guests</div>
              <div className="text-3xl font-bold text-white">{analyticsData.guestStats.total}</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Meeting Volume */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
              <h3 className="text-white font-semibold mb-4">Meeting Volume</h3>
              <div className="h-48 flex items-end gap-1">
                {analyticsData.meetingsByDay.length > 0 ? (
                  analyticsData.meetingsByDay.slice(-14).map((day, i) => {
                    const maxCount = Math.max(...analyticsData.meetingsByDay.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-[#06c] to-purple-500 rounded-t opacity-80 hover:opacity-100 transition-opacity"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${day.date}: ${day.count} meetings`}
                      />
                    );
                  })
                ) : (
                  <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
                    No data available
                  </div>
                )}
              </div>
              <div className="flex justify-between mt-2 text-white/40 text-xs">
                <span>{analyticsData.meetingsByDay[0]?.date?.slice(5) || ''}</span>
                <span>{analyticsData.meetingsByDay[analyticsData.meetingsByDay.length - 1]?.date?.slice(5) || ''}</span>
              </div>
            </div>

            {/* Peak Hours */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
              <h3 className="text-white font-semibold mb-4">Peak Hours</h3>
              <div className="h-48 flex items-end gap-0.5">
                {analyticsData.peakHours.map((count, hour) => {
                  const maxCount = Math.max(...analyticsData.peakHours, 1);
                  const height = (count / maxCount) * 100;
                  const isActive = hour >= 9 && hour <= 17;
                  return (
                    <div
                      key={hour}
                      className={`flex-1 rounded-t transition-opacity ${
                        isActive
                          ? 'bg-gradient-to-t from-green-500 to-emerald-400'
                          : 'bg-gradient-to-t from-white/20 to-white/10'
                      }`}
                      style={{ height: `${Math.max(height, 3)}%` }}
                      title={`${formatHour(hour)}: ${count} meetings`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-white/40 text-xs">
                <span>12AM</span>
                <span>12PM</span>
                <span>11PM</span>
              </div>
            </div>
          </div>

          {/* Top Contributors */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 p-6">
            <h3 className="text-white font-semibold mb-4">Top Meeting Hosts</h3>
            <div className="space-y-3">
              {analyticsData.topHosts.length > 0 ? (
                analyticsData.topHosts.map((host, i) => {
                  const maxCount = analyticsData.topHosts[0]?.count || 1;
                  const percentage = (host.count / maxCount) * 100;
                  return (
                    <div key={host.userId} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-medium text-sm">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm">{host.email?.split('@')[0]}</span>
                          <span className="text-white/60 text-sm">{host.count} meetings</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#06c] to-purple-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-white/40 text-sm">
                  No meeting data available yet
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
